import formatMessage from 'format-message';

// Standard block types mapped from strings
const BlockType = {
    COMMAND: 'command',
    REPORTER: 'reporter',
    BOOLEAN: 'Boolean',
    HAT: 'hat'
};

const ArgumentType = {
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'Boolean'
};

// Simple polyfill for scratch-vm Cast.toString
const Cast = {
    toString: val => String(val || '')
};

/**
 * Icon URI (white microphone on transparent background) for block icons.
 * @type {string}
 */
// eslint-disable-next-line max-len
const blockIconURI = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI4IiBmaWxsPSIjMEZCRDhDIi8+PHBhdGggZD0iTTIwIDI0YzIuMjEgMCAzLjk5LTEuNzkgMy45OS00TDI0IDEyYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0djhjMCAyLjIxIDEuNzkgNCA0IDR6bTYuNi00YzAgNC0zLjM4IDYuOC03LjA3IDYuOFMxMy40IDI0IDEzLjQgMjBIMTFjMCA0LjU1IDMuNjMgOC4zMSA4IDguOTVWMzNoMnYtNC4wNWM0LjM3LS42MyA4LTQuNCA4LTguOTVoLTIuNHoiIGZpbGw9IiNGRkYiLz48L3N2Zz4=';

/**
 * Icon for the extension category in the toolbox.
 * @type {string}
 */
// eslint-disable-next-line max-len
const menuIconURI = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTIgMTRjMS42NiAwIDIuOTktMS4zNCAyLjk5LTNMMTUgNWMwLTEuNjYtMS4zNC0zLTMtM1M5IDMuMzQgOSA1djZjMCAxLjY2IDEuMzQgMyAzIDN6bTUuMy0zYzAgMy0yLjU0IDUuMS01LjMgNS4xUzYuNyAxNCA2LjcgMTFINWMwIDMuNDEgMi43MiA2LjIzIDYgNi43MlYyMWgydi0zLjI4YzMuMjgtLjQ4IDYtMy4zIDYtNi43MmgtMS43eiIgZmlsbD0iIzBGQkQ4QyIvPjxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz48L3N2Zz4=';

/**
 * Language menu items for speech recognition.
 */
const LANGUAGE_MENU = [
    {text: 'English', value: 'en-US'},
    {text: 'Hindi', value: 'hi-IN'},
    {text: 'Spanish', value: 'es-ES'},
    {text: 'French', value: 'fr-FR'},
    {text: 'German', value: 'de-DE'},
    {text: 'Chinese (Mandarin)', value: 'zh-CN'},
    {text: 'Japanese', value: 'ja-JP'},
    {text: 'Korean', value: 'ko-KR'},
    {text: 'Portuguese', value: 'pt-BR'},
    {text: 'Arabic', value: 'ar-SA'},
    {text: 'Russian', value: 'ru-RU'},
    {text: 'Italian', value: 'it-IT'}
];

/**
 * Speech Recognition extension for Scratch — uses the browser's
 * built-in Web Speech API (SpeechRecognition / webkitSpeechRecognition).
 *
 * Blocks:
 *  - "listen and wait"                (command)
 *  - "start listening"                (command, non-blocking)
 *  - "stop listening"                 (command)
 *  - "when I hear [PHRASE]"           (hat / event)
 *  - "speech"                         (reporter — last transcription)
 *  - "is listening?"                  (boolean reporter)
 *  - "set language to [LANG]"         (command)
 *  - "speech confidence"              (reporter 0-100)
 */
class Scratch3SpeechRecognitionBlocks {
    constructor (runtime) {
        this.runtime = runtime;

        /**
         * The last recognised utterance.
         * @type {string}
         */
        this._currentUtterance = '';

        /**
         * Utterance copy used for edge-triggering "when I hear" hats.
         * Reset at the start of each listen cycle so the same word can
         * re-trigger in the next cycle.
         * @type {string}
         */
        this._utteranceForEdgeTrigger = '';

        /**
         * Confidence of the last result (0–1 from the API, exposed as 0–100).
         * @type {number}
         */
        this._confidence = 0;

        /**
         * Whether recognition is currently active.
         * @type {boolean}
         */
        this._listening = false;

        /**
         * Language / locale code sent to the speech engine.
         * @type {string}
         */
        this._language = 'en-US';

        /**
         * Queue of resolve-callbacks from "listen and wait" blocks.
         * @type {Function[]}
         */
        this._speechPromises = [];

        /**
         * The SpeechRecognition instance (lazy-initialised).
         * @type {SpeechRecognition|null}
         */
        this._recognition = null;

        // Clean up on stop-all (red stop button).
        this.runtime.on('PROJECT_STOP_ALL', () => {
            this._stopListening();
            this._currentUtterance = '';
            this._utteranceForEdgeTrigger = '';
            this._confidence = 0;
        });

        // Reset edge triggers on green flag.
        this.runtime.on('PROJECT_START', () => {
            this._utteranceForEdgeTrigger = '';
        });
    }

    // ------------------------------------------------------------------
    //  Internal helpers
    // ------------------------------------------------------------------

    /**
     * Lazy-create a SpeechRecognition instance, configure it and
     * wire up event handlers.
     * @private
     */
    _ensureRecognition () {
        if (this._recognition) return;

        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            console.warn('Speech Recognition API is not supported in this browser.');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.lang = this._language;

        recognition.onresult = event => {
            const result = event.results[event.results.length - 1];
            if (result.isFinal || result[0].confidence > 0.5) {
                const transcript = result[0].transcript.trim();
                
                // Set the current utterance for reporter blocks
                this._currentUtterance = transcript;
                
                // Temporarily clear edge trigger so it can fire even if it's the same phrase
                this._utteranceForEdgeTrigger = '';
                
                // Allow VM edge detection to notice the false -> true transition
                setTimeout(() => {
                    this._utteranceForEdgeTrigger = transcript;
                }, 50);

                this._confidence = result[0].confidence;
                console.log(`[SpeechRecognition] "${transcript}" (${(this._confidence * 100).toFixed(1)}%)`);
                this._resolveSpeechPromises();
            }
        };

        recognition.onerror = event => {
            // "no-speech" and "aborted" are normal — don't spam the console.
            if (event.error !== 'no-speech' && event.error !== 'aborted') {
                console.warn(`[SpeechRecognition] error: ${event.error}`);
            }
            this._listening = false;
            this._resolveSpeechPromises();
        };

        recognition.onend = () => {
            this._listening = false;
            // Resolve any remaining promises (e.g. nothing was heard).
            this._resolveSpeechPromises();
        };

        this._recognition = recognition;
    }

    /**
     * Begin speech recognition (non-blocking helper).
     * @private
     */
    _startListening () {
        this._ensureRecognition();
        if (!this._recognition) {
            // API not available — resolve immediately.
            this._resolveSpeechPromises();
            return;
        }
        // If already listening, abort and restart.
        if (this._listening) {
            try {
                this._recognition.abort();
            } catch (_) { /* ignore */ }
        }
        this._recognition.lang = this._language;
        this._utteranceForEdgeTrigger = '';
        try {
            this._recognition.start();
            this._listening = true;
            console.log('[SpeechRecognition] Listening...');
        } catch (e) {
            console.warn(`[SpeechRecognition] Could not start: ${e.message}`);
            this._listening = false;
            this._resolveSpeechPromises();
        }
    }

    /**
     * Stop speech recognition.
     * @private
     */
    _stopListening () {
        if (this._recognition && this._listening) {
            try {
                this._recognition.stop();
            } catch (_) { /* ignore */ }
        }
        this._listening = false;
    }

    /**
     * Resolve all queued promises (from "listen and wait" blocks).
     * @private
     */
    _resolveSpeechPromises () {
        const promises = this._speechPromises.splice(0);
        for (const resolve of promises) {
            resolve();
        }
    }

    /**
     * Simple case-insensitive substring match.
     * @param {string} pattern
     * @param {string} text
     * @returns {boolean}
     * @private
     */
    _speechMatches (pattern, text) {
        if (!pattern || !text) return false;
        return text.toLowerCase().includes(pattern.toLowerCase());
    }

    // ------------------------------------------------------------------
    //  Extension metadata
    // ------------------------------------------------------------------

    getInfo () {
        return {
            id: 'speechRecognition',
            name: formatMessage({
                id: 'speechRecognition.extensionName',
                default: 'Speech Recognition',
                description: 'Speech Recognition extension'
            }),
            menuIconURI: menuIconURI,
            blockIconURI: blockIconURI,
            color1: '#D65C5C',
            color2: '#B94A4A',
            color3: '#B94A4A',
            blocks: [
                {
                    opcode: 'setAutoClearing',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.turnAutoClearing',
                        default: 'turn [STATE] auto clearing'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOffMenu',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'setClearResultsAfterSpeech',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.clearResultsTime',
                        default: 'clear results after [TIME] seconds of speech'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TIME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'infinite'
                        }
                    }
                },
                {
                    opcode: 'setClearResultsAfterPause',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.clearResultsPause',
                        default: 'clear results after a pause of [TIME] seconds'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TIME: {
                            type: ArgumentType.STRING,
                            defaultValue: 'infinite'
                        }
                    }
                },
                {
                    opcode: 'setLanguage',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.setLanguage',
                        default: 'set language to [LANG]'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        LANG: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'en-US'
                        }
                    }
                },
                {
                    opcode: 'resetSettings',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.resetSettings',
                        default: 'reset settings'
                    }),
                    blockType: BlockType.COMMAND
                },
                '---',
                {
                    opcode: 'listenAndWait',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.listenAndWait',
                        default: 'listen and wait'
                    }),
                    blockType: BlockType.COMMAND
                },
                {
                    opcode: 'startListening',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.startListening',
                        default: 'start listening'
                    }),
                    blockType: BlockType.COMMAND
                },
                {
                    opcode: 'stopListening',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.stopListening',
                        default: 'stop listening'
                    }),
                    blockType: BlockType.COMMAND
                },
                {
                    opcode: 'getSpeechResult',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.getResult',
                        default: 'speech recognition result'
                    }),
                    blockType: BlockType.REPORTER
                },
                {
                    opcode: 'clearSpeechResult',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.clearResult',
                        default: 'clear speech recognition result'
                    }),
                    blockType: BlockType.COMMAND
                },
                {
                    opcode: 'isListening',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.isListening',
                        default: 'is listening?'
                    }),
                    blockType: BlockType.BOOLEAN
                },
                {
                    opcode: 'getConfidence',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.getConfidence',
                        default: 'speech confidence'
                    }),
                    blockType: BlockType.REPORTER
                },
                {
                    opcode: 'whenIHear',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.whenIHear',
                        default: 'when I hear [PHRASE]'
                    }),
                    blockType: BlockType.HAT,
                    isEdgeActivated: true,
                    arguments: {
                        PHRASE: {
                            type: ArgumentType.STRING,
                            defaultValue: 'hello'
                        }
                    }
                }
            ],
            menus: {
                onOffMenu: {
                    acceptReporters: true,
                    items: ['on', 'off']
                },
                languageMenu: {
                    acceptReporters: true,
                    items: [
                        {text: 'English (India)', value: 'en-IN'},
                        {text: 'English (US)', value: 'en-US'},
                        {text: 'English (UK)', value: 'en-GB'},
                        {text: 'Hindi', value: 'hi-IN'},
                        {text: 'Spanish', value: 'es-ES'},
                        {text: 'French', value: 'fr-FR'},
                        {text: 'German', value: 'de-DE'},
                        {text: 'Chinese (Mandarin)', value: 'zh-CN'},
                        {text: 'Japanese', value: 'ja-JP'}
                    ]
                }
            }
        };
    }

    // ------------------------------------------------------------------
    //  Block implementations
    // ------------------------------------------------------------------

    startListening () {
        if (!this._listening) {
            this._startListening();
        }
    }

    listenAndWait () {
        return new Promise(resolve => {
            this._speechPromises.push(resolve);
            if (!this._listening) {
                this._startListening();
            }
        });
    }

    stopListening () {
        this._stopListening();
    }

    getSpeechResult () {
        return this._currentUtterance;
    }

    clearSpeechResult () {
        this._currentUtterance = '';
    }

    isListening () {
        return this._listening;
    }

    getConfidence () {
        return Math.round(this._confidence * 100);
    }

    whenIHear (args) {
        return this._speechMatches(
            Cast.toString(args.PHRASE),
            this._utteranceForEdgeTrigger
        );
    }

    setAutoClearing (args) {
        // Implementation for auto clearing toggle
    }

    setClearResultsAfterSpeech (args) {
        // Implementation for timeout
    }

    setClearResultsAfterPause (args) {
        // Implementation for pause
    }

    resetSettings () {
        // Implementation for resetting all settings
    }

    setLanguage (args) {
        this._language = Cast.toString(args.LANG);
        if (this._recognition) {
            this._recognition.lang = this._language;
        }
    }
}

export default Scratch3SpeechRecognitionBlocks;
